import axios from 'axios';
import { uploadToS3 } from './s3Upload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

export { apiClient };

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('elevate360_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('elevate360_token');
      localStorage.removeItem('elevate360_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  sendOtp: (data) => apiClient.post('/auth/send-otp', data),
  verifyOtp: (data) => apiClient.post('/auth/verify-otp', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  processOAuthSession: (sessionId) => apiClient.post('/auth/session', { session_id: sessionId }),
  verifyCredentials: (data) => apiClient.post('/auth/verify-credentials', data),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
};

// Users API
export const usersAPI = {
  getAll: () => apiClient.get('/users/'),
  getById: (userId) => apiClient.get(`/users/${userId}`),
  updateRole: (userId, role) => apiClient.put(`/users/${userId}/role?role=${role}`),
  updateStatus: (userId, isActive) => apiClient.put(`/users/${userId}/status?is_active=${isActive}`),
  setPassword: (userId, newPassword) => apiClient.put(`/users/${userId}/password`, { new_password: newPassword }),
  updateEmail: (userId, email) => apiClient.put(`/users/${userId}/email`, { email }),
  updateProfile: (userId, data) => apiClient.put(`/users/${userId}`, data),
  delete: (userId) => apiClient.delete(`/users/${userId}`),
};

// Courses API
export const coursesAPI = {
  getAll: () => apiClient.get('/courses/'),
  getById: (courseId) => apiClient.get(`/courses/${courseId}`),
  create: (data) => apiClient.post('/courses/', data),
  update: (courseId, data) => apiClient.put(`/courses/${courseId}`, data),
  delete: (courseId) => apiClient.delete(`/courses/${courseId}`),
};

// Cohorts API
export const cohortsAPI = {
  getAll: (courseId = null) => apiClient.get('/cohorts/', { params: courseId ? { course_id: courseId } : {} }),
  getById: (cohortId) => apiClient.get(`/cohorts/${cohortId}`),
  create: (data) => apiClient.post('/cohorts/', data),
  update: (cohortId, data) => apiClient.put(`/cohorts/${cohortId}`, data),
  delete: (cohortId) => apiClient.delete(`/cohorts/${cohortId}`),
};

// Enrollments API
export const enrollmentsAPI = {
  getUserEnrollments: (userId) => apiClient.get(`/enrollments/user/${userId}`),
  getCohortEnrollments: (cohortId) => apiClient.get(`/enrollments/cohort/${cohortId}`),
  getCourseEnrollments: (courseId) => apiClient.get(`/enrollments/course/${courseId}`),
  create: (data) => apiClient.post('/enrollments/', data),
  delete: (enrollmentId) => apiClient.delete(`/enrollments/${enrollmentId}`),
  updateDriveFolder: (enrollmentId, driveFolder) => apiClient.put(`/enrollments/${enrollmentId}/drive-folder`, { personal_drive_folder: driveFolder }),
  getMyEnrollments: () => apiClient.get('/enrollments/my-enrollments'),
  getMy: () => apiClient.get('/enrollments/my-enrollments'),
  payInstallment: (enrollmentId, seq, data) =>
    apiClient.post(`/enrollments/${enrollmentId}/installments/${seq}/pay`, data),
  recordPayment: (enrollmentId, payment) =>
    apiClient.post(`/enrollments/${enrollmentId}/payment-plan`, payment),
  markInstallmentPaid: (enrollmentId, seq) =>
    apiClient.put(`/enrollments/${enrollmentId}/installments/${seq}/mark-paid`),
};

// Assignments API
export const assignmentsAPI = {
  getAll: (cohortId = null, courseId = null) => apiClient.get('/assignments/', {
    params: {
      ...(cohortId ? { cohort_id: cohortId } : {}),
      ...(courseId ? { course_id: courseId } : {}),
    },
  }),
  getById: (assignmentId) => apiClient.get(`/assignments/${assignmentId}`),
  create: (data) => apiClient.post('/assignments/', data),
  update: (assignmentId, data) => apiClient.put(`/assignments/${assignmentId}`, data),
  delete: (assignmentId) => apiClient.delete(`/assignments/${assignmentId}`),
};

// Submissions API
export const submissionsAPI = {
  getMy: () => apiClient.get('/submissions/my'),
  getByAssignment: (assignmentId) => apiClient.get(`/submissions/assignment/${assignmentId}`),
  getById: (submissionId) => apiClient.get(`/submissions/${submissionId}`),
  create: (data) => apiClient.post('/submissions/', data),
  grade: (submissionId, data) => apiClient.put(`/submissions/${submissionId}/grade`, data),
  reopen: (submissionId, note = '') => apiClient.put(`/submissions/${submissionId}/reopen`, { note }),
  delete: (submissionId) => apiClient.delete(`/submissions/${submissionId}`),
};

// Admin API
export const adminAPI = {
  getStats: () => apiClient.get('/admin/stats'),
  getPendingSubmissions: () => apiClient.get('/admin/submissions/pending'),
  enrollUserToCourse: (userId, courseId, payment) =>
    apiClient.post('/admin/enroll-user-to-course', {
      user_id: userId, course_id: courseId,
      ...(payment ? { payment } : {}),
    }),
  getAuditLog: (params = {}) => apiClient.get('/admin/audit-log', { params }),
  getUserActivity: (params = {}) => apiClient.get('/admin/user-activity', { params }),
  getBackendErrors: (params = {}) => apiClient.get('/admin/errors', { params }),
  getEmailLog: (params = {}) => apiClient.get('/admin/emails', { params }),
  sendTestEmail: (payload) => apiClient.post('/admin/test-email', payload),
};

// Email outbox (admin)
export const emailOutboxAPI = {
  list: (params = {}) => apiClient.get('/admin/notifications', { params }),
  stats: () => apiClient.get('/admin/notifications/stats'),
  get: (id) => apiClient.get(`/admin/notifications/${id}`),
  resend: (id) => apiClient.post(`/admin/notifications/${id}/resend`),
  retryFailed: (payload) => apiClient.post('/admin/notifications/retry-failed', payload),
  compose: (payload) => apiClient.post('/admin/notifications/compose', payload),
  sendForRecord: (payload) => apiClient.post('/admin/notifications/send-for-record', payload),
  templates: () => apiClient.get('/admin/notifications/templates'),
  legacy: (params = {}) => apiClient.get('/admin/notifications/legacy', { params }),
  missed: (params = {}) => apiClient.get('/admin/notifications/missed', { params }),
  sendMissed: (payload) => apiClient.post('/admin/notifications/missed/send', payload),
  composeTemplates: () => apiClient.get('/admin/notifications/compose-templates'),
  createComposeTemplate: (payload) => apiClient.post('/admin/notifications/compose-templates', payload),
  deleteComposeTemplate: (id) => apiClient.delete(`/admin/notifications/compose-templates/${id}`),
};

// Team alerts: which internal team members get which event emails
export const teamAlertsAPI = {
  get: () => apiClient.get('/admin/team-alerts'),
  save: (payload) => apiClient.put('/admin/team-alerts', payload),
};

// Payments API
export const paymentsAPI = {
  getCoursePricing: (courseId) => apiClient.get(`/payments/course-pricing/${courseId}`),
  createCheckout: (courseId, paymentType = 'one_time') => apiClient.post('/payments/create-checkout', {
    course_id: courseId,
    origin_url: window.location.origin,
    payment_type: paymentType,
  }),
  getStatus: (sessionId) => apiClient.get(`/payments/status/${sessionId}`),
  // Admin endpoints
  getAllTransactions: () => apiClient.get('/payments/admin/transactions'),
  updateCoursePricing: (courseId, data) => apiClient.put(`/payments/admin/course-pricing/${courseId}`, data),
  getVATReport: () => apiClient.get('/payments/admin/vat-report'),
  listPayments: (params = {}) => apiClient.get('/admin/payments', { params }),
  getPaymentDetail: (id) => apiClient.get(`/admin/payments/${id}`),
  deletePayment: (id) => apiClient.delete(`/admin/payments/${id}`),
  refundPayment: (id, body) => apiClient.post(`/admin/payments/${id}/refund`, body),
  markFake: (kind, value) => apiClient.post('/admin/payments/fake', { kind, value }),
  unmarkFake: (kind, value) => apiClient.delete('/admin/payments/fake', { params: { kind, value } }),
  listSubscriptions: (params = {}) => apiClient.get('/admin/subscriptions', { params }),
  cancelSubscription: (id) => apiClient.post(`/admin/subscriptions/${id}/cancel`),
  getReconciliation: () => apiClient.get('/admin/reconciliation'),
  getAnalytics: (params = {}) => apiClient.get('/admin/analytics', { params }),
  exportTransactionsCsv: (params = {}) => apiClient.get('/admin/payments/export.csv', { params, responseType: 'blob' }),
  getArrears: () => apiClient.get('/admin/payments/arrears'),
};

// Chat API
export const chatAPI = {
  getMessages: (courseId, limit = 50, before = null) => {
    const params = { limit };
    if (before) params.before = before;
    return apiClient.get(`/chat/course/${courseId}/messages`, { params });
  },
  sendMessage: (courseId, content, file = null) => {
    const formData = new FormData();
    formData.append('content', content);
    if (file) formData.append('file', file);
    return apiClient.post(`/chat/course/${courseId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteMessage: (messageId) => apiClient.delete(`/chat/messages/${messageId}`),
  getChatInfo: (courseId) => apiClient.get(`/chat/course/${courseId}/info`),
};

// Files API
export const filesAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (fileId) => apiClient.get(`/files/${fileId}`),
  
  // Course file management
  initCourseFolders: (courseId) => apiClient.post(`/files/courses/${courseId}/init-folders`),
  getCourseFolders: (courseId) => apiClient.get(`/files/courses/${courseId}/folders`),
  createCourseFolder: (courseId, data) => apiClient.post(`/files/courses/${courseId}/folders`, data),
  updateCourseFolder: (courseId, folderId, data) => apiClient.put(`/files/courses/${courseId}/folders/${folderId}`, data),
  deleteCourseFolder: (courseId, folderId) => apiClient.delete(`/files/courses/${courseId}/folders/${folderId}`),
  toggleFolderLock: (courseId, folderId, isLocked) => apiClient.put(`/files/courses/${courseId}/folders/${folderId}/lock`, { is_locked: isLocked }),
  getCourseFolderFiles: (courseId, folderId) => apiClient.get(`/files/courses/${courseId}/folders/${folderId}/files`),
  uploadCourseFile: async (courseId, folderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/files/courses/${courseId}/folders/${folderId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addCourseFolderLink: (courseId, folderId, data) =>
    apiClient.post(`/files/courses/${courseId}/folders/${folderId}/link`, data),
  downloadCourseFile: (courseId, fileId) => apiClient.get(`/files/courses/${courseId}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteCourseFile: (courseId, fileId) => apiClient.delete(`/files/courses/${courseId}/files/${fileId}`),
  
  // User file management (admin)
  initUserFolders: (userId) => apiClient.post(`/files/users/${userId}/init-folders`),
  getUserFolderTree: (userId) => apiClient.get(`/files/users/${userId}/folder-tree`),
  createUserFolder: (userId, data) => apiClient.post(`/files/users/${userId}/folders`, data),
  updateUserFolder: (userId, folderId, data) => apiClient.put(`/files/users/${userId}/folders/${folderId}`, data),
  deleteUserFolder: (userId, folderId) => apiClient.delete(`/files/users/${userId}/folders/${folderId}`),
  getUserFolderFiles: (userId, folderId) => apiClient.get(`/files/users/${userId}/folders/${folderId}/files`),
  uploadUserFile: async (userId, folderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/files/users/${userId}/folders/${folderId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Delegate file management (own files)
  getMyFolders: () => apiClient.get('/files/my/folders'),
  getMyFolderFiles: (folderId) => apiClient.get(`/files/my/folders/${folderId}/files`),
  submitAssignment: async (assignmentId, file) => {
    const formData = new FormData();
    formData.append('assignment_id', assignmentId);
    formData.append('file', file);
    return apiClient.post('/files/my/submit-assignment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMySubmissions: () => apiClient.get('/files/my/submissions'),
  downloadMyFile: (fileId) => apiClient.get(`/files/my/files/${fileId}/download`, { responseType: 'blob' }),
  downloadMySubmission: (submissionId) => apiClient.get(`/files/my/submissions/${submissionId}/download`, { responseType: 'blob' }),
  resubmitAssignment: async (submissionId, file) => {
    const formData = new FormData();
    formData.append('submission_id', submissionId);
    formData.append('file', file);
    return apiClient.post('/files/my/resubmit-assignment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Admin submission management
  getAllSubmissions: (status, cohortId) => apiClient.get('/files/admin/submissions', { 
    params: { status, cohort_id: cohortId } 
  }),
  downloadSubmission: (submissionId) => apiClient.get(`/files/admin/submissions/${submissionId}/download`, { responseType: 'blob' }),
  gradeSubmission: (submissionId, data) => apiClient.put(`/files/admin/submissions/${submissionId}/grade`, data),
  adminUploadResubmission: async (submissionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/files/admin/submissions/${submissionId}/upload-resubmission`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  returnGradedFile: async (submissionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/files/admin/submissions/${submissionId}/return-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Drive API (Mock)
export const driveAPI = {
  getStatus: () => apiClient.get('/drive/status'),
  getFolders: () => apiClient.get('/drive/folders'),
};

// Certificates API
export const certificatesAPI = {
  getMy: () => apiClient.get('/certificates/my'),
  getByUser: (userId) => apiClient.get(`/certificates/user/${userId}`),
  issue: (enrollmentId) => apiClient.post(`/certificates/issue/${enrollmentId}`),
  download: (certificateId) => apiClient.get(`/certificates/download/${certificateId}`, { responseType: 'blob' }),
  verify: (certificateNumber) => apiClient.get(`/certificates/verify/${certificateNumber}`),
};

// Mentors API
export const mentorsAPI = {
  // Admin endpoints
  getAll: () => apiClient.get('/mentors/'),
  assignCohort: (mentorId, cohortId) => apiClient.post('/mentors/assign-cohort', { mentor_id: mentorId, cohort_id: cohortId }),
  unassignCohort: (assignmentId) => apiClient.delete(`/mentors/unassign-cohort/${assignmentId}`),
  getCohortAssignments: (mentorId = null, cohortId = null) => {
    const params = {};
    if (mentorId) params.mentor_id = mentorId;
    if (cohortId) params.cohort_id = cohortId;
    return apiClient.get('/mentors/cohort-assignments', { params });
  },
  
  // Mentor-only endpoints
  getMyCohorts: () => apiClient.get('/mentors/my-cohorts'),
  getMyDelegates: () => apiClient.get('/mentors/my-delegates'),
  getDashboardStats: () => apiClient.get('/mentors/dashboard-stats'),
  getPendingReviews: () => apiClient.get('/mentors/pending-reviews'),
  getRecentSubmissions: (limit = 10) => apiClient.get('/mentors/recent-submissions', { params: { limit } }),
  downloadSubmission: (submissionId) => apiClient.get(`/mentors/submissions/${submissionId}/download`, { responseType: 'blob' }),
  getCohortSubmissions: (cohortId) => apiClient.get(`/mentors/cohort/${cohortId}/submissions`),
  reviewSubmission: (submissionId, data) => apiClient.put(`/mentors/review/${submissionId}`, data),
  getNotifications: () => apiClient.get('/mentors/notifications'),
  getUnreadCount: () => apiClient.get('/mentors/notifications/unread-count'),
  markNotificationRead: (notificationId) => apiClient.put(`/mentors/notifications/${notificationId}/mark-read`),
  
  // Assignment management for mentors
  getCohortAssignmentsForMentor: (cohortId) => apiClient.get(`/mentors/cohort/${cohortId}/assignments`),
  createAssignment: (cohortId, data) => apiClient.post(`/mentors/cohort/${cohortId}/assignments`, data),
  updateAssignment: (cohortId, assignmentId, data) => apiClient.put(`/mentors/cohort/${cohortId}/assignments/${assignmentId}`, data),
};

// Request review for submissions
export const submissionsAPIExtended = {
  ...submissionsAPI,
  requestReview: (submissionId, message = null) => apiClient.put(`/submissions/${submissionId}/request-review`, { message }),
};

// Modules API
export const modulesAPI = {
  getCourseModules: (courseId) => apiClient.get(`/modules/course/${courseId}`),
  create: (data) => apiClient.post('/modules/', data),
  update: (moduleId, data) => apiClient.put(`/modules/${moduleId}`, data),
  delete: (moduleId) => apiClient.delete(`/modules/${moduleId}`),
  unlock: (moduleId) => apiClient.post(`/modules/${moduleId}/unlock`),
  lock: (moduleId) => apiClient.post(`/modules/${moduleId}/lock`),
  complete: (moduleId) => apiClient.post(`/modules/${moduleId}/complete`),
};

// Streaks API
export const streaksAPI = {
  getMy: () => apiClient.get('/streaks/my'),
  recordActivity: () => apiClient.post('/streaks/record-activity'),
};

// Notifications API
export const notificationsAPI = {
  getMy: (limit = 20, offset = 0) => apiClient.get('/users/me/notifications', { params: { limit, offset } }),
  markRead: (notificationId) => apiClient.post(`/users/me/notifications/${notificationId}/mark-read`),
  markAllRead: () => apiClient.post('/users/me/notifications/mark-all-read'),
};

// Dashboard API
export const dashboardAPI = {
  getData: () => apiClient.get('/users/me/dashboard-data'),
};

// Leads API (public and admin)
export const leadsAPI = {
  // Public endpoints (no auth)
  getPopupConfig: () => axios.get(`${BACKEND_URL}/api/leads/popup-config`),
  captureLead: (data) => axios.post(`${BACKEND_URL}/api/leads/capture`, data),
  // Admin endpoints
  getAll: () => apiClient.get('/leads/'),
  updateStatus: (leadId, data) => apiClient.put(`/leads/${leadId}/status`, data),
  deleteLead: (leadId) => apiClient.delete(`/leads/${leadId}`),
  updatePopupConfig: (config) => apiClient.put('/leads/popup-config', config),
  exportCSV: () => apiClient.post('/leads/export', {}, { responseType: 'blob' }),
};

// Testimonials API (public and admin)
export const testimonialsAPI = {
  // Public endpoints
  getVideoTestimonials: () => axios.get(`${BACKEND_URL}/api/testimonials/videos`),
  // Admin endpoints
  getAllAdmin: () => apiClient.get('/testimonials/videos/admin'),
  create: (data) => apiClient.post('/testimonials/videos', data),
  update: (id, data) => apiClient.put(`/testimonials/videos/${id}`, data),
  delete: (id) => apiClient.delete(`/testimonials/videos/${id}`),
  reorder: (order) => apiClient.post('/testimonials/videos/reorder', { order }),
};

// Site Settings API
export const siteSettingsAPI = {
  getAll: () => apiClient.get('/site-settings/all'),
  getSection: (section) => apiClient.get(`/site-settings/section/${section}`),
  updateSection: (section, data) => apiClient.put(`/site-settings/section/${section}`, data),
  getMentors: () => apiClient.get('/site-settings/mentors'),
  createMentor: (data) => apiClient.post('/site-settings/mentors', data),
  updateMentor: (mentorId, data) => apiClient.put(`/site-settings/mentors/${mentorId}`, data),
  deleteMentor: (mentorId) => apiClient.delete(`/site-settings/mentors/${mentorId}`),
};

// Referrals API
export const referralsAPI = {
  getMyCode: () => apiClient.get('/referrals/my-referral-code'),
  getMyReferrals: () => apiClient.get('/referrals/my-referrals'),
  refer: (data) => apiClient.post('/referrals/refer', data),
  validateCode: (code) => apiClient.post('/referrals/validate-code', { code }),
  getMyRewards: () => apiClient.get('/referrals/my-rewards'),
  claimReward: (rewardId, data) => apiClient.post(`/referrals/claim-reward/${rewardId}`, data),
  // Admin
  getAllReferrals: () => apiClient.get('/referrals/admin/all'),
  getTopReferrers: () => apiClient.get('/referrals/admin/top-referrers'),
  issueLabVoucher: (data) => apiClient.post('/referrals/admin/issue-lab-voucher', data),
};

// Email Sequences API
export const emailSequencesAPI = {
  getAll: () => apiClient.get('/email-sequences/sequences'),
  get: (sequenceId) => apiClient.get(`/email-sequences/sequences/${sequenceId}`),
  create: (data) => apiClient.post('/email-sequences/sequences', data),
  update: (sequenceId, data) => apiClient.put(`/email-sequences/sequences/${sequenceId}`, data),
  delete: (sequenceId) => apiClient.delete(`/email-sequences/sequences/${sequenceId}`),
  toggle: (sequenceId) => apiClient.post(`/email-sequences/sequences/${sequenceId}/toggle`),
  getQueue: () => apiClient.get('/email-sequences/queue'),
  getAnalytics: () => apiClient.get('/email-sequences/analytics'),
  trigger: (triggerType, data) => apiClient.post(`/email-sequences/trigger/${triggerType}`, data),
};

// AI Assistant API
export const aiAssistantApi = {
  chat: (data) => apiClient.post('/ai-assistant/chat', data),
  getHistory: (courseId) => apiClient.get(`/ai-assistant/history/${courseId}`),
};

// Programmes API
export const programmesAPI = {
  // Public
  getPublicList: () => apiClient.get('/programmes/public'),
  getPublicBySlug: (slug) => apiClient.get(`/programmes/public/${slug}`),
  
  // Admin
  getAll: () => apiClient.get('/programmes/'),
  create: (data) => apiClient.post('/programmes/', data),
  update: (programmeId, data) => apiClient.put(`/programmes/${programmeId}`, data),
  delete: (programmeId) => apiClient.delete(`/programmes/${programmeId}`),
  toggleActive: (programmeId) => apiClient.put(`/programmes/${programmeId}/toggle-active`),
  
  // Media
  getPresignedUploadUrl: (programmeId, data) => 
    apiClient.post(`/programmes/${programmeId}/presigned-upload-url`, data),
  
  // Payments
  createBookingCheckout: (slug, data) => 
    apiClient.post(`/programmes/${slug}/create-booking-checkout`, data),
  getBookingStatus: (sessionId) => apiClient.get(`/programmes/booking-status/${sessionId}`),
  getAdminBookings: () => apiClient.get('/programmes/admin/bookings'),
  
  // User Bookings
  getMyBookings: () => apiClient.get('/programmes/my-bookings'),
  payBalance: (slug, bookingId, data) =>
    apiClient.post(`/programmes/${slug}/pay-balance/${bookingId}`, data),
  setupInstallments: (slug, bookingId, data) =>
    apiClient.post(`/programmes/${slug}/setup-installments/${bookingId}`, data),
  selectMonthlyPlan: (slug, bookingId) =>
    apiClient.post(`/programmes/${slug}/bookings/${bookingId}/select-monthly-plan`),
  payInstallment: (slug, bookingId, seq, data) =>
    apiClient.post(`/programmes/${slug}/bookings/${bookingId}/pay-installment/${seq}`, data),
  settleRemaining: (slug, bookingId, data) =>
    apiClient.post(`/programmes/${slug}/bookings/${bookingId}/settle-remaining`, data),

  // Waitlist
  joinWaitlist: (data) => apiClient.post('/programmes/waitlist', data),
  getAdminWaitlist: () => apiClient.get('/programmes/admin/waitlist'),

  // Booking OTP
  sendBookingOtp: (data) => apiClient.post('/auth/send-otp', { ...data, name: '', password: '', purpose: 'booking' }),
  verifyBookingOtp: (data) => apiClient.post('/auth/verify-otp', { ...data, purpose: 'booking' }),
};

export const eventsAPI = {
  // Public
  getPublicList: (params) => apiClient.get('/events/public', { params }),
  getPublicBySlug: (slug) => apiClient.get(`/events/public/${slug}`),

  // Admin
  getAll: () => apiClient.get('/events/'),
  create: (data) => apiClient.post('/events/', data),
  update: (eventId, data) => apiClient.put(`/events/${eventId}`, data),
  delete: (eventId) => apiClient.delete(`/events/${eventId}`),
  toggleActive: (eventId) => apiClient.put(`/events/${eventId}/toggle-active`),
  getPresignedUploadUrl: (eventId, data) =>
    apiClient.post(`/events/${eventId}/presigned-upload-url`, data),
};

export const galleryAPI = {
  // Public
  getPublic: () => apiClient.get('/gallery/public'),

  // Admin
  getAll: () => apiClient.get('/gallery/'),
  getPresignedUploadUrl: (data) => apiClient.post('/gallery/presigned-upload-url', data),
  register: (images) => apiClient.post('/gallery/', { images }),
  update: (imageId, data) => apiClient.put(`/gallery/${imageId}`, data),
  delete: (imageId) => apiClient.delete(`/gallery/${imageId}`),
  reorder: (imageIds) => apiClient.put('/gallery/reorder', { image_ids: imageIds }),
  getCategories: () => apiClient.get('/gallery/categories'),
  saveCategories: (categories) => apiClient.put('/gallery/categories', { categories }),
  addVideo: (data) => apiClient.post('/gallery/videos', data),
  updateVideo: (videoId, data) => apiClient.put(`/gallery/videos/${videoId}`, data),
  deleteVideo: (videoId) => apiClient.delete(`/gallery/videos/${videoId}`),
  reorderVideos: (videoIds) => apiClient.put('/gallery/videos/reorder', { video_ids: videoIds }),
};

// Migration / Database Mirroring API
export const migrationAPI = {
  getSummary: () => apiClient.get('/migrate/summary'),
};

// Blogs API
export const blogsAPI = {
  // Public
  getAll: (params = {}) => apiClient.get('/blogs/', { params }),
  getBySlug: (slug) => apiClient.get(`/blogs/${slug}`),
  getCategories: () => apiClient.get('/blogs/categories'),

  // Admin
  getAllAdmin: () => apiClient.get('/blogs/admin'),
  getByIdAdmin: (blogId) => apiClient.get(`/blogs/admin/${blogId}`),
  create: (data) => apiClient.post('/blogs/', data),
  update: (blogId, data) => apiClient.put(`/blogs/${blogId}`, data),
  delete: (blogId) => apiClient.delete(`/blogs/${blogId}`),
  togglePublish: (blogId) => apiClient.put(`/blogs/${blogId}/toggle-publish`),

  // Categories (admin)
  createCategory: (data) => apiClient.post('/blogs/categories', data),
  deleteCategory: (categoryId) => apiClient.delete(`/blogs/categories/${categoryId}`),

  // Author library (admin)
  getAuthors: () => apiClient.get('/blogs/authors'),
  createAuthor: (data) => apiClient.post('/blogs/authors', data),
  updateAuthor: (authorId, data) => apiClient.put(`/blogs/authors/${authorId}`, data),
  deleteAuthor: (authorId) => apiClient.delete(`/blogs/authors/${authorId}`),

  // Cover image upload (presign → PUT to S3)
  presignImage: (data) => apiClient.post('/blogs/upload-image/presign', data),
};

// Upload a blog cover image to S3. Returns the public URL string; throws if
// S3 rejects the PUT, so a failed upload never yields a broken image URL.
export const uploadBlogImage = async (file) => {
  const { data } = await blogsAPI.presignImage({
    filename: file.name,
    content_type: file.type,
    file_size: file.size,
  });
  await uploadToS3(data.presigned_url, file, data.content_type);
  return data.public_url;
};

export default apiClient;
